export interface AuthErrorMessage {
  /** Translation key looked up in the CRM message catalog. */
  key: string;
  /** English text used when the key is missing from the catalog. */
  fallback: string;
}

/**
 * Maps a Supabase auth error code to the message shown on the login page.
 *
 * The code reaches us through the URL fragment of a failed email link
 * (`#error=access_denied&error_code=otp_expired&...`), so it is
 * attacker-controllable. It therefore only ever *selects* one of our own
 * messages -- the `error_description` that Supabase puts next to it is never
 * displayed, since anything rendered from that fragment would let a crafted
 * link put arbitrary text in front of a user on our sign-in page.
 *
 * The copy stays neutral about how to get a new link: the same failure covers
 * both password recovery (the user can request another from "Forgot password?")
 * and invitations (only an administrator can re-send those), and the error
 * fragment carries no `type` to tell them apart.
 */
export function resolveAuthErrorMessage(errorCode: string): AuthErrorMessage {
  switch (errorCode) {
    case "otp_expired":
      return {
        key: "crm.auth.email_link_expired",
        fallback:
          "This link has expired or has already been used. Please request a new one.",
      };
    default:
      return {
        key: "crm.auth.email_link_invalid",
        fallback: "This link is not valid. Please request a new one.",
      };
  }
}
